import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterSelector } from '@/core/components/FilterSelector/FilterSelector';
import { LocationSearchSelect } from './components/LocationSearchSelect/LocationSearchSelect';
import { type LocationOption } from './components/LocationSearchSelect/LocationSearchSelect';
import { type SelectionMap } from '@/core/components/FilterSelector/types';
import { SelectionSummary } from '@/core/components/SelectionSummary';
import {
  type OriginData,
  type FilterOption,
  LANGUAGE_OPTIONS,
  toLabelsSelection,
  toIdsSelection,
} from './OriginContentDefaults';
import { dispatchFilterEvent } from '../../GlobalFilterModal';
import './OriginContent.scss';

interface OriginContentProps {
  values: OriginData;
  onChange: (data: OriginData) => void;
  onCountChange?: (count: number) => void;
  availableChannels: FilterOption[];
  channelSelectorDisabled?: boolean;
}

export const OriginContent: React.FC<OriginContentProps> = ({
  values,
  onChange,
  onCountChange,
  availableChannels,
  channelSelectorDisabled = false,
}) => {
  const { t } = useTranslation();
  const { channelsSelection, languagesSelection, selectedLocations, languagesTouched = false, channelsTouched = false } = values;

  useEffect(() => {
    if (!onCountChange) return;

    // The language and channels selectors start fully selected ("Todos") by default, and
    // clicking the "Todos" option also selects everything - both cases are equivalent to "no
    // filter applied" (nothing is sent to the API for a full selection), so only count them
    // when a partial (not full "select all") selection is active.
    const isPartialSelection = (selection: SelectionMap, availableCount: number) => {
      const selectedCount = Object.keys(selection).length;
      if (selectedCount === 0) return false;
      // If the available options aren't known yet (e.g. channels still loading), we can't tell
      // whether this is a full "select all" - assume it's filtered rather than hiding a real filter.
      if (availableCount <= 0) return true;
      return selectedCount < availableCount;
    };
    const isLanguagesFiltered = languagesTouched && isPartialSelection(languagesSelection, LANGUAGE_OPTIONS.length);
    const isChannelsFiltered = channelsTouched && isPartialSelection(channelsSelection, availableChannels.length);

    const count =
      (isChannelsFiltered ? 1 : 0) +
      (selectedLocations.length > 0 ? 1 : 0) +
      (isLanguagesFiltered ? 1 : 0);

    onCountChange(count);
  }, [
    channelsSelection,
    selectedLocations,
    languagesSelection,
    languagesTouched,
    channelsTouched,
    availableChannels.length,
    onCountChange,
  ]);

  const channelLabels = useMemo(
    () => availableChannels.map((ch) => ch.label),
    [availableChannels],
  );

  const channelsLabelSelection = useMemo(
    () => toLabelsSelection(channelsSelection, availableChannels),
    [channelsSelection, availableChannels],
  );

  const languageFilterOptions = useMemo<FilterOption[]>(
    () => LANGUAGE_OPTIONS.map((opt) => ({ id: opt.id, label: t(opt.i18nKey) })),
    [t],
  );

  const languageLabels = useMemo(
    () => languageFilterOptions.map((opt) => opt.label),
    [languageFilterOptions],
  );

  const languagesLabelSelection = useMemo(
    () => toLabelsSelection(languagesSelection, languageFilterOptions),
    [languagesSelection, languageFilterOptions],
  );

  const handleChannelsChange = useCallback(
    (updater: React.SetStateAction<SelectionMap>) => {
      const currentLabels = toLabelsSelection(channelsSelection, availableChannels);
      const nextLabels = typeof updater === 'function' ? updater(currentLabels) : updater;
      const nextIds = toIdsSelection(nextLabels, availableChannels);
      onChange({ ...values, channelsSelection: nextIds, channelsTouched: true });
      dispatchFilterEvent('ORIGIN', 'CHANNELS', nextIds);
    },
    [availableChannels, channelsSelection, values, onChange],
  );

  const handleLanguagesChange = useCallback(
    (updater: React.SetStateAction<SelectionMap>) => {
      const currentLabels = toLabelsSelection(languagesSelection, languageFilterOptions);
      const nextLabels = typeof updater === 'function' ? updater(currentLabels) : updater;
      const nextIds = toIdsSelection(nextLabels, languageFilterOptions);
      onChange({ ...values, languagesSelection: nextIds, languagesTouched: true });
      dispatchFilterEvent('ORIGIN', 'LANGUAGES', nextIds);
    },
    [languageFilterOptions, languagesSelection, values, onChange],
  );

  const handleLocationsChange = useCallback(
    (nextLocations: LocationOption[]) => {
      onChange({ ...values, selectedLocations: nextLocations });
      dispatchFilterEvent('ORIGIN', 'LOCATIONS', nextLocations);
    },
    [values, onChange],
  );

  return (
    <div className="origin-content">
      <div className={`origin-content__channel-group${channelSelectorDisabled ? ' origin-content__channel-group--disabled' : ''}`}>
        <FilterSelector
          label={t('globalFilter.channels')}
          placeholder={t('globalFilter.placeholderChannels')}
          availableItems={channelLabels}
          selection={channelsLabelSelection}
          onChange={handleChannelsChange}
          allowNegation={false}
          disabled={channelSelectorDisabled}
        />
        <SelectionSummary selection={channelsSelection} availableItemsCount={availableChannels.length} />
      </div>

      <div className="origin-content__location-group">
        <LocationSearchSelect
          label={t('globalFilter.locations')}
          placeholder={t('globalFilter.placeholderLocations')}
          helperText={t('globalFilter.infoText')}
          loadingText={t('globalFilter.loadingLocations')}
          emptyText={t('globalFilter.emptyLocations')}
          selectedLocations={selectedLocations}
          onSelectionChange={handleLocationsChange}
        />
        <SelectionSummary items={selectedLocations} />
      </div>

      <div>
        <FilterSelector
          label={t('globalFilter.languageDetected')}
          placeholder={t('globalFilter.placeHolderLanguageDetected')}
          availableItems={languageLabels}
          selection={languagesLabelSelection}
          onChange={handleLanguagesChange}
          allowNegation={false}
        />
        <SelectionSummary selection={languagesSelection} availableItemsCount={LANGUAGE_OPTIONS.length} />
      </div>
    </div>
  );
};
